import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import {
  Link2,
  Plus,
  ExternalLink,
  Edit3,
  Trash2,
  Image,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Axios already configured with interceptors in AuthContext

export default function Pages() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    url: "",
    icon_url: "",
  });

  const fetchPages = useCallback(async () => {
    if (!navigator.onLine) {
      toast.error("You are offline");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/pages`);
      setPages(response.data || []);
    } catch (error) {
      toast.error("Failed to load pages");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.url.trim()) {
      toast.error("Name and URL are required");
      return;
    }

    if (!navigator.onLine) {
      toast.error("You are offline");
      return;
    }

    try {
      if (editingPage) {
        await axios.put(`${API}/pages/${editingPage.id}`, formData);
        toast.success("Page updated");
      } else {
        await axios.post(`${API}/pages`, formData);
        toast.success("Page created");
      }
      resetForm();
      fetchPages();
    } catch (error) {
      toast.error("Operation failed");
    }
  };

  const handleDelete = async () => {
    if (!navigator.onLine) {
      toast.error("You are offline");
      return;
    }

    try {
      await axios.delete(`${API}/pages/${editingPage.id}`);
      toast.success("Page deleted");
      setShowDelete(false);
      setEditingPage(null);
      fetchPages();
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  const resetForm = () => {
    setFormData({ name: "", url: "", icon_url: "" });
    setEditingPage(null);
    setShowForm(false);
  };

  const openEdit = (page) => {
    setEditingPage(page);
    setFormData({
      name: page.name,
      url: page.url,
      icon_url: page.icon_url || "",
    });
    setShowForm(true);
  };

  const openDelete = (page) => {
    setEditingPage(page);
    setShowDelete(true);
  };

  return (
    <AdminLayout title="PAGES">
      <div className="p-4" data-testid="pages-manager">
        {/* Toolbar */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-[#22c55e] font-['VT323'] text-xl">
            [ EXTERNAL LINKS ]
          </h2>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#22c55e] text-[#050505] hover:bg-[#4ade80] transition-colors"
            data-testid="add-page-btn"
          >
            <Plus size={16} />
            Add Page
          </button>
        </div>

        {/* Pages List */}
        {loading ? (
          <div className="text-[#22c55e] text-center py-8">[ LOADING... ]</div>
        ) : pages.length === 0 ? (
          <div className="text-center py-12 text-[#4ade80] opacity-60">
            <Link2 size={48} className="mx-auto mb-4" />
            <p>No pages yet. Add your first link!</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {pages.map((page) => (
              <div
                key={page.id}
                className="flex items-center gap-4 p-4 bg-[#0a0a0a] border border-[#15803d] hover:border-[#22c55e] transition-colors"
                data-testid={`page-item-${page.id}`}
              >
                {/* Icon */}
                <div className="w-12 h-12 flex items-center justify-center bg-[#050505] border border-[#15803d] flex-shrink-0">
                  {page.icon_url ? (
                    <img
                      src={page.icon_url}
                      alt={page.name}
                      className="w-8 h-8 object-contain"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <Link2 size={20} className="text-[#22c55e]" />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-[#22c55e] font-mono truncate">
                    {page.name}
                  </h3>
                  <p className="text-[#4ade80] text-sm truncate opacity-70">
                    {page.url}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <a
                    href={page.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-[#06b6d4] hover:bg-[#06b6d4]/10 transition-colors"
                    title="Open link"
                  >
                    <ExternalLink size={18} />
                  </a>
                  <button
                    onClick={() => openEdit(page)}
                    className="p-2 text-[#f59e0b] hover:bg-[#f59e0b]/10 transition-colors"
                    title="Edit"
                    data-testid={`edit-page-${page.id}`}
                  >
                    <Edit3 size={18} />
                  </button>
                  <button
                    onClick={() => openDelete(page)}
                    className="p-2 text-[#ef4444] hover:bg-[#ef4444]/10 transition-colors"
                    title="Delete"
                    data-testid={`delete-page-${page.id}`}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showForm} onOpenChange={(open) => !open && resetForm()}>
          <DialogContent className="dialog-content">
            <DialogHeader>
              <DialogTitle className="text-[#22c55e] font-['VT323'] text-xl">
                [ {editingPage ? "EDIT" : "NEW"} PAGE ]
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="block text-[#06b6d4] text-sm uppercase tracking-wider mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Page name..."
                  className="w-full bg-transparent border border-[#15803d] text-[#22c55e] px-4 py-3 focus:border-[#22c55e] focus:outline-none font-mono"
                  data-testid="page-name-input"
                />
              </div>
              <div>
                <label className="block text-[#06b6d4] text-sm uppercase tracking-wider mb-2">
                  URL *
                </label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, url: e.target.value }))
                  }
                  placeholder="https://..."
                  className="w-full bg-transparent border border-[#15803d] text-[#22c55e] px-4 py-3 focus:border-[#22c55e] focus:outline-none font-mono"
                  data-testid="page-url-input"
                />
              </div>
              <div>
                <label className="block text-[#06b6d4] text-sm uppercase tracking-wider mb-2">
                  Icon URL (optional)
                </label>
                <input
                  type="url"
                  value={formData.icon_url}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, icon_url: e.target.value }))
                  }
                  placeholder="https://... (favicon or logo)"
                  className="w-full bg-transparent border border-[#15803d] text-[#22c55e] px-4 py-3 focus:border-[#22c55e] focus:outline-none font-mono"
                  data-testid="page-icon-input"
                />
              </div>
              {formData.icon_url && (
                <div className="flex items-center gap-2 text-sm text-[#4ade80]">
                  <Image size={14} />
                  <span>Preview:</span>
                  <img
                    src={formData.icon_url}
                    alt="icon preview"
                    className="w-6 h-6 object-contain"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <button
                onClick={resetForm}
                className="px-4 py-2 border border-[#15803d] text-[#4ade80]"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-[#22c55e] text-[#050505]"
                data-testid="save-page-btn"
              >
                {editingPage ? "Update" : "Create"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
          <AlertDialogContent className="dialog-content">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-[#ef4444] font-['VT323'] text-xl">
                [ CONFIRM DELETE ]
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[#4ade80]">
                Are you sure you want to delete "{editingPage?.name}"?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border border-[#15803d] text-[#4ade80] bg-transparent hover:bg-[#15803d]/20">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-[#ef4444] text-white hover:bg-[#dc2626]"
                data-testid="confirm-delete-page-btn"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
